import type { ErrorEvent } from '@sentry/core';

export const SENTRY_DSN_ADMIN = 'https://e6ced77cc723478fad969f5f3ba00b06@o969560.ingest.us.sentry.io/5920728';
export const SENTRY_DSN_CLIENT = 'https://7871f506431356d58e794c27951994d4@o969560.ingest.us.sentry.io/5920728';

export const SENTRY_RELEASE = `story-cards@${process.env.VERSION}`;

export const SENTRY_APP = {
	admin: 'admin',
	client: 'client',
} as const;

const sentThisSession = new Set<string>();

function oncePerSession(key: string): boolean {
	if (sentThisSession.has(key)) return false;
	try {
		const storageKey = `sentry:once:${key}`;
		if (sessionStorage.getItem(storageKey)) {
			sentThisSession.add(key);
			return false;
		}
		sessionStorage.setItem(storageKey, '1');
	} catch {
		// private mode: cap to this page lifetime only
	}
	sentThisSession.add(key);
	return true;
}

function errorText(event: ErrorEvent): string {
	const exception = event.exception?.values?.[0];
	return [exception?.type, exception?.value, event.message].filter(Boolean).join(' ');
}

function flooderKey(text: string): string | null {
	if (/ChunkLoadError|Loading CSS chunk|Loading chunk \d+ failed/i.test(text)) return 'chunk-load';
	if (/localStorage/i.test(text)) return 'localstorage';
	return null;
}

export const sentryDenyUrls = [/extensions\//i, /^chrome:\/\//i, /extension:/i];

export const sentryClientDenyUrls = [
	...sentryDenyUrls,
	/widgetapp/i,
	/webkit-masked-url/i, // https://github.com/getsentry/sentry-javascript/discussions/5875
];

export const sentryClientIgnoreErrors = [
	/gtag/i,
	/googletag/i,
	/accessibility/i,
	/kWidget/i,
	/vcmidOfContent/i,
	/Walla/i,
	/makoCatDFP/i,
	/sentryWrapped/i,
	/@sentry/i,
	/webkit-masked-url/i,
	/zp_token is not defined/i,
	/Blocked 'connect' from .*google-analytics/i,
	/window\.webkit\.messageHandlers/i,
	/Error invoking postMessage: Java object is gone/i,
	/User is unregistered/i,
];

export function beforeSendClient(event: ErrorEvent): ErrorEvent | null {
	const stacktrace = event.exception?.values?.[0]?.stacktrace?.frames;
	if (stacktrace && stacktrace.some(frame => frame.filename === '<anonymous>' || !frame.filename)) {
		return null;
	}

	const exception = event.exception?.values?.[0];
	if (exception?.value === 'Load failed' && !exception?.stacktrace) {
		const crumb = event.breadcrumbs?.[0];
		const isMetadata404 =
			crumb?.type === 'http' && crumb?.data?.url?.endsWith('/metadata.json') && crumb?.data?.status_code === 404;
		if (isMetadata404) return null;
	}

	const key = flooderKey(errorText(event));
	if (key && !oncePerSession(key)) return null;

	return event;
}
